<template>
    <div class="report-page">
        <!-- Filters and Export -->
        <div class="dropdown-container">
                <!-- Distributor -->
                <div class="dropdown-item">
                    <label class="form-label font-weight-bold">Distributor</label>
                    <select 
                        class="form-select" 
                        v-model="selectedDistributorId"
                        :disabled="userRole && userRole.toLowerCase() === 'distributor'"
                    >
                        <option disabled selected value="">Select Distributor</option>
                        <option value="All">All Distributors</option>
                        <option v-for="dist in distributorList" :key="dist.profile_id" :value="dist.profile_id">
                            {{ dist.full_name || dist.name || dist.username || dist.first_name }}
                        </option>
                    </select>
                </div>

                <!-- Dealer Type -->
                <div class="dropdown-item">
                    <label class="form-label font-weight-bold">Dealer Type</label>
                    <select class="form-select" v-model="selectedDealerType">
                        <option disabled selected value="">Select Dealer Type</option>
                        <option v-for="type in dealerTypes" :key="type.id" :value="type.id">
                            {{ type.name }}
                        </option>
                    </select>
                </div>

                <!-- Dealer Category -->
                <div class="dropdown-item" v-if="selectedDealerType === 'Individual'">
                    <label class="form-label font-weight-bold">Dealer Category</label>
                    <select class="form-select" v-model="selectedDealerCategoryId">
                        <option disabled selected value="">Select Dealer Category</option>
                        <option value="">All Dealer Categories</option>
                        <option v-for="cat in dealerCategoryList" :key="cat.id" :value="cat.id">
                            {{ cat.dealer_cat_name }}
                        </option>
                    </select>
                </div>

                <!-- Dealer Group -->
                <div class="dropdown-item" v-if="selectedDealerType === 'Groups'">
                    <label class="form-label font-weight-bold">Dealer Group</label>
                    <select class="form-select" v-model="selectedDealerGroupId">
                        <option disabled selected value="">Select Dealer Group</option>
                        <option value="">All Dealer Groups</option>
                        <option v-for="group in dealerGroupsList" :key="group.id || group.group_id" :value="group.id || group.group_id">
                            {{ group.name || group.group_name }}
                        </option>
                    </select>
                </div>

                <!-- Search + Export Buttons -->
                <div class="search-container">
                    <button type="button" class="button-default" :disabled="loading" @click="handleSearch">
                        <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
                        {{ loading ? "Searching..." : "Search" }}
                    </button>
                    <button type="button" class="btn button-export" @click="exportToExcel" :disabled="!reportData.length">
                        Export to Excel
                    </button>
                </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-spinner mt-3">
            <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>

        <!-- Data Table -->
        <div v-if="!loading" class="table-responsive w-100 mt-4">
            <table class="table table-bordered table-hover">
                <thead class="table-light">
                    <tr>
                        <th>Dealer Name</th>
                        <th class="text-end">Today</th>
                        <th class="text-end">1-14</th>
                        <th class="text-end">15-28</th>
                        <th class="text-end">29-42</th>
                        <th class="text-end">43-56</th>
                        <th class="text-end">57-70</th>
                        <th class="text-end">71-90</th>
                        <th class="text-end">90+</th>
                        <th class="text-end font-weight-bold">Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="dealer in reportData" :key="dealer.id" :class="{ 'inactive-row': Number(dealer.is_active) === 0 }">
                        <td>{{ dealer.name }}</td>
                        <td class="text-end">{{ formatAmount(dealer.day_0) }}</td>
                        <td class="text-end">{{ formatAmount(dealer.d1_14) }}</td>
                        <td class="text-end">{{ formatAmount(dealer.d15_28) }}</td>
                        <td class="text-end">{{ formatAmount(dealer.d29_42) }}</td>
                        <td class="text-end">{{ formatAmount(dealer.d43_56) }}</td>
                        <td class="text-end">{{ formatAmount(dealer.d57_70) }}</td>
                        <td class="text-end">{{ formatAmount(dealer.d71_90) }}</td>
                        <td class="text-end">{{ formatAmount(dealer.d90_plus) }}</td>
                        <td class="text-end font-weight-bold">{{ formatAmount(dealer.total) }}</td>
                    </tr>
                    <tr v-if="reportData.length === 0">
                        <td colspan="10" class="text-center text-muted">No data available</td>
                    </tr>
                </tbody>
                <tfoot v-if="reportData.length > 0 && totals" class="table-light font-weight-bold">
                    <tr>
                        <td>Total</td>
                        <td class="text-end">{{ formatAmount(totals.day_0) }}</td>
                        <td class="text-end">{{ formatAmount(totals.d1_14) }}</td>
                        <td class="text-end">{{ formatAmount(totals.d15_28) }}</td>
                        <td class="text-end">{{ formatAmount(totals.d29_42) }}</td>
                        <td class="text-end">{{ formatAmount(totals.d43_56) }}</td>
                        <td class="text-end">{{ formatAmount(totals.d57_70) }}</td>
                        <td class="text-end">{{ formatAmount(totals.d71_90) }}</td>
                        <td class="text-end">{{ formatAmount(totals.d90_plus) }}</td>
                        <td class="text-end">{{ formatAmount(totals.total) }}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script>
import * as XLSX from "xlsx";
import {fetchDistributors, getDealerCategories, getDealerGroups, fetchAgeingAnalysisReport } from "@/services/dataService";

export default {
    name: "AgeingAnalysisReportForm",
    data() {
        return {
            token: localStorage.getItem("token_set") || "",
            profileId: localStorage.getItem("profile_id") || "",
            userRole: localStorage.getItem("user_label") || "",

            dealerTypes: [
                { id: 'Individual', name: 'Individual' },
                { id: 'Groups', name: 'Groups' }
            ],
            distributorList: [],
            dealerCategoryList: [],
            dealerGroupsList: [],

            selectedDealerType: "Individual",
            selectedDistributorId: "",
            selectedDealerCategoryId: "",
            selectedDealerGroupId: "",

            loading: false,

            reportData: [],
            totals: null,
        };
    },
    computed: {
        
    },
    watch: {
        selectedDistributorId(newVal) {
            this.selectedDealerGroupId = "";
            this.dealerGroupsList = [];
            if (newVal) {
                this.loadDealerGroups(newVal);
            }
        },
        selectedDealerType() {
            this.selectedDealerCategoryId = "";
            this.selectedDealerGroupId = "";
        }
    },
    mounted() {
        this.loadInitialData();
    },
    methods: {
        async loadInitialData() {
            this.loading = true;
            try {
                await Promise.all([
                    this.loadDistributors(),
                    this.loadDealerCategories()
                ]);

                await this.handleSearch();
            } catch (error) {
                console.error("Error loading initial data:", error);
                this.loading = false;
            }
        },
        async loadDistributors() {
            try {
                const response = await fetchDistributors(this.profileId, this.token);
                if (response.data && (response.data.status === "success" || response.data.status === true || response.data.status === 200)) {
                    this.distributorList = response.data.data || [];
                    
                    if (this.userRole && this.userRole.toLowerCase() === 'distributor') {
                        const dist = this.distributorList.find(d => String(d.profile_id) === String(this.profileId));
                        if (dist) {
                            this.selectedDistributorId = dist.profile_id;
                        } else if (this.distributorList.length > 0) {
                            this.selectedDistributorId = this.distributorList[0].profile_id;
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching distributors:", error);
            }
        },
        async loadDealerCategories() {
            try {
                const response = await getDealerCategories(this.token);
                if (response.data && (response.data.status === "success" || response.data.status === true || response.data.status === 200)) {
                    this.dealerCategoryList = response.data.data || response.data; 
                }
            } catch (error) {
                console.error("Error fetching dealer categories:", error);
            }
        },
        async loadDealerGroups(distributorId) {
            try {
                const response = await getDealerGroups(distributorId, this.token);
                if (response.data && (response.data.status === "success" || response.data.status === true || response.data.status === 200)) {
                    this.dealerGroupsList = response.data.data || response.data;
                }
            } catch (error) {
                console.error("Error fetching dealer groups:", error);
            }
        },
        async handleSearch() {
            this.loading = true;

            try {
                const payload = {
                    profile_id: Number(this.profileId),
                    dealer_type: this.selectedDealerType,
                    distributor_id:
                        this.selectedDistributorId && this.selectedDistributorId !== "All"
                            ? this.selectedDistributorId
                            : null,
                    dealer_category_id:
                        this.selectedDealerType === "Individual" && this.selectedDealerCategoryId
                            ? this.selectedDealerCategoryId
                            : null,
                    dealer_group_id:
                        this.selectedDealerType === "Groups" && this.selectedDealerGroupId
                            ? this.selectedDealerGroupId
                            : null,
                };

                const response = await fetchAgeingAnalysisReport(this.token, payload);

                if (response.data && response.data.status === "success") {
                    this.reportData = response.data.data || [];
                    this.totals = response.data.totals || null;
                } else {
                    this.reportData = [];
                    this.totals = null;
                    console.error("Error loading Ageing Analysis Report:", response.data?.message);
                }

                this.$emit('search', payload);
            } catch (error) {
                this.reportData = [];
                this.totals = null;
                console.error("Error fetching ageing analysis report:", error);
            } finally {
                this.loading = false;
            }
        },
        formatAmount(value) {
            if (value === undefined || value === null) return '0.00';
            return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        exportToExcel() {
            if (!this.reportData.length) {
                return;
            }

            const bucketLabels = {
                day_0: 'Today',
                d1_14: '1-14',
                d15_28: '15-28',
                d29_42: '29-42',
                d43_56: '43-56',
                d57_70: '57-70',
                d71_90: '71-90',
                d90_plus: '90+',
            };
            const bucketKeys = Object.keys(bucketLabels);

            // ===== FILTER SECTION =====
            const distributorLabel = this.selectedDistributorId === 'All'
                ? 'All Distributors'
                : (this.distributorList.find(d => String(d.profile_id) === String(this.selectedDistributorId))?.full_name
                    || this.distributorList.find(d => String(d.profile_id) === String(this.selectedDistributorId))?.name
                    || 'All');

            const categoryLabel = this.selectedDealerCategoryId
                ? (this.dealerCategoryList.find(c => String(c.id) === String(this.selectedDealerCategoryId))?.dealer_cat_name || '')
                : 'All Dealer Categories';

            const groupLabel = this.selectedDealerGroupId
                ? (this.dealerGroupsList.find(g => String(g.id || g.group_id) === String(this.selectedDealerGroupId))?.name
                    || this.dealerGroupsList.find(g => String(g.id || g.group_id) === String(this.selectedDealerGroupId))?.group_name
                    || '')
                : 'All Dealer Groups';

            const filterData = [
                ["Ageing Analysis Report"],
                [],
                ["Distributor", distributorLabel],
                ["Dealer Type", this.selectedDealerType],
                ...(this.selectedDealerType === 'Individual' ? [["Dealer Category", categoryLabel]] : []),
                ...(this.selectedDealerType === 'Groups' ? [["Dealer Group", groupLabel]] : []),
                [],
            ];

            // ===== TABLE DATA =====
            const exportData = this.reportData.map(dealer => {
                const row = { "Dealer Name": dealer.name };
                bucketKeys.forEach(key => {
                    row[bucketLabels[key]] = dealer[key] || 0;
                });
                row["Total"] = dealer.total || 0;
                return row;
            });

            if (this.totals) {
                const totalRow = { "Dealer Name": "Total" };
                bucketKeys.forEach(key => {
                    totalRow[bucketLabels[key]] = this.totals[key] || 0;
                });
                totalRow["Total"] = this.totals.total || 0;
                exportData.push(totalRow);
            }

            // Convert to worksheet
            const ws = XLSX.utils.aoa_to_sheet(filterData);

            const tableStartRow = filterData.length + 1;
            XLSX.utils.sheet_add_json(ws, exportData, { origin: `A${tableStartRow}` });

            const firstName = localStorage.getItem("first_name") || "";
            const lastName = localStorage.getItem("last_name") || "";
            const generatedBy = `${firstName} ${lastName}`.trim();
            const generatedAt = new Date().toLocaleString();

            const generatedRows = [
                [],
                ["Generated By", generatedBy],
                ["Generated At", generatedAt],
            ];

            const tableEndRow = tableStartRow + exportData.length + 1;
            XLSX.utils.sheet_add_aoa(ws, generatedRows, { origin: `A${tableEndRow}` });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, ws, "Ageing Analysis");

            const today = new Date().toISOString().split("T")[0];
            XLSX.writeFile(workbook, `Ageing_Analysis_Report_${today}.xlsx`);
        }
    }
}
</script>

<style scoped>
@import '@/utils/buttons.css';

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: calc(100vh - 100px);
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.btn.button-export {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #28a745;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.btn.button-export:hover {
  background-color: #218838;
}

.btn.button-export:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.table > tbody > tr.inactive-row > td {
  background-color: #ffebee !important;
  box-shadow: none !important;
}
</style>